import { useInventoryPageSummary } from '@/hooks/useInventory';
import { useShop } from '@/hooks/useShop';
import { deriveStorefrontLaunchState } from '@/lib/storefront-launch';

// Polling cadence while we wait for the first upload. Ingest is batched at
// 60s and the Typesense sync at 30s, so a file that has landed shows up in
// the count within a couple of minutes — 15s keeps the "products arriving"
// counter ticking visibly without hammering the summary endpoint. That
// cadence only makes sense for OAuth connections, which download on connect;
// a plug-and-play / partner-lead / custom source is waiting on a cron tick or
// a human, measured in hours or days, so it polls once a minute instead.
export const LAUNCH_READINESS_POLL_MS = 15000;
export const LAUNCH_READINESS_SLOW_POLL_MS = 60000;

export const launchPollInterval = (launch) => {
	if (!launch.waiting) return 0;
	return launch.connectType === 'oauth' ? LAUNCH_READINESS_POLL_MS : LAUNCH_READINESS_SLOW_POLL_MS;
};

/**
 * Launch-readiness for the Local Storefront, shared by every launch CTA
 * (Overview card, Channels welcome, storefront ChannelDetail, Inventory
 * first-sync card). Polls the inventory page summary while the shop is still
 * waiting on its first upload so the CTA unlocks — and the product count
 * fills — without a reload. Polling stops as soon as the gate opens.
 *
 * Pass `null` once the storefront is already live: there is nothing left to
 * unlock, and a live shop whose feed has dropped to zero in-stock items would
 * otherwise keep polling for as long as the page is open.
 */
export function useStorefrontLaunchReadiness(shopId) {
	const { shop } = useShop();
	const { summary, isLoading } = useInventoryPageSummary(shopId, undefined, {
		refreshInterval: (latest) => launchPollInterval(deriveStorefrontLaunchState({ summary: latest, shop }))
	});
	return deriveStorefrontLaunchState({ summary, shop, summaryLoading: isLoading && !summary });
}
